import type { KagemaneTemplate } from './kagemane-catalog';

export const KAGEMANE_GALLERY_GAP = 16;
export const KAGEMANE_LIBRARY_COLUMNS = 4;

/** Native auto sizes selects by the actual lazy card width and device pixel ratio. */
export function kagemaneCoverProps(item: Pick<KagemaneTemplate, 'poster' | 'previewPoster' | 'previewPosterSources'>) {
  const sources = item.previewPosterSources;
  return {
    src: item.previewPoster || item.poster,
    srcSet: sources?.map(source => `${source.url} ${source.width}w`).join(', ') || undefined,
    // Native auto tracks the rendered card; the fallback also supports older browsers.
    sizes: sources?.length ? 'auto, (min-width: 768px) 496px, 50vw' : undefined,
  };
}

/** Pack whole cards into the earliest available space without changing their DOM order. */
export function layoutKagemaneCards(width: number, heights: readonly number[], desktop: boolean, completeRowsOnly = false, columnCount?: number, columnSpans?: readonly number[]) {
  if (!Number.isFinite(width) || width <= 0 || heights.length === 0) {
    return { columnWidth: 0, widths: [], positions: [], height: 0 };
  }
  // Full libraries keep a consistent column count and scale cards to their pane.
  const availableColumns = columnCount && Number.isFinite(columnCount) && columnCount > 0
    ? Math.max(1, Math.floor(columnCount))
    : desktop
    ? Math.max(1, Math.floor((width + KAGEMANE_GALLERY_GAP) / (240 + KAGEMANE_GALLERY_GAP)))
    : width > KAGEMANE_GALLERY_GAP ? 2 : 1;
  // A finite homepage preview must not leave an almost-empty final row.
  // Full libraries keep every card.
  const columns = completeRowsOnly ? Math.min(availableColumns, heights.length) : availableColumns;
  const visibleHeights = completeRowsOnly ? heights.slice(0, Math.floor(heights.length / columns) * columns) : heights;
  const columnWidth = Math.max(0, (width - (columns - 1) * KAGEMANE_GALLERY_GAP) / columns);
  const occupied: { column: number; span: number; top: number; bottom: number }[] = [];
  const widths: number[] = [];
  const positions = visibleHeights.map((height, index) => {
    const requested = columnSpans?.[index] ?? 1;
    const span = Number.isFinite(requested) ? Math.min(columns, Math.max(1, Math.floor(requested))) : 1;
    const safeHeight = Number.isFinite(height) && height > 0 ? height : 0;
    let column = 0;
    let top = Infinity;
    // Search gaps as well as column bottoms: a spanning card must not prevent
    // later portraits from filling unused space above it.
    for (let start = 0; start <= columns - span; start++) {
      let candidateTop = 0;
      for (const box of occupied) {
        if (box.column >= start + span || box.column + box.span <= start) continue;
        if (candidateTop + safeHeight + KAGEMANE_GALLERY_GAP <= box.top) break;
        candidateTop = Math.max(candidateTop, box.bottom + KAGEMANE_GALLERY_GAP);
      }
      if (candidateTop < top) { column = start; top = candidateTop; }
    }
    widths.push(columnWidth * span + KAGEMANE_GALLERY_GAP * (span - 1));
    const box = { column, span, top, bottom: top + safeHeight };
    const next = occupied.findIndex(item => item.top > top);
    occupied.splice(next < 0 ? occupied.length : next, 0, box);
    return { left: column * (columnWidth + KAGEMANE_GALLERY_GAP), top };
  });
  return { columnWidth, widths, positions, height: Math.max(...occupied.map(box => box.bottom)) };
}

export function kagemaneAspectRatio(ratio?: number): number {
  return typeof ratio === 'number' && Number.isFinite(ratio) && ratio > 0 ? ratio : 9 / 16;
}
